const milestones = [
  { year: '1970', event: 'Custom Clothiers founded at our Wellesley, MA showroom — a small bespoke studio built entirely on craft, precision, and personal care.' },
  { year: '1985', event: 'Expanded to full bespoke suit and custom shirt services, adding Italian mill fabrics and formal tailoring to our offering.' },
  { year: '2000', event: 'Launched our signature house and office call program — bringing premium tailoring directly to clients across Massachusetts.' },
  { year: '2010', event: 'Recognized among Boston\'s top custom tailors, serving executives, professionals, and discerning clients statewide.' },
  { year: '2020', event: 'Celebrated over 3,500 satisfied clients and a half-century of uninterrupted craftsmanship and community trust.' },
  { year: '2024+', event: 'Continuing to serve Massachusetts and New England with the same passion, precision, and personal touch as day one.' },
];

const values = [
  { icon: 'ri-heart-line', title: 'Passion for Craft', desc: 'Every garment is made with genuine love for the centuries-old art of tailoring and a relentless commitment to excellence.' },
  { icon: 'ri-shield-check-line', title: 'Uncompromising Quality', desc: 'We use only the finest fabrics and construction techniques — no shortcuts, no compromises, ever.' },
  { icon: 'ri-user-heart-line', title: 'Client-First Service', desc: 'Your satisfaction is our only measure of success. We listen deeply before we ever pick up the scissors.' },
  { icon: 'ri-map-pin-line', title: 'Deep Local Roots', desc: 'Proudly serving Wellesley and the Massachusetts community for over 55 years — built on trust and relationships.' },
];

const CompanyStorySection = () => {
  return (
    <>
      {/* Story */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <div>
              <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-5">
                Our Story
              </p>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                A Legacy Built on Fit, Fabric, and Relationships
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed mb-5">
                Custom Clothiers was born from a simple belief that has never changed: every person
                deserves clothing that fits their body, reflects their personality, and stands the
                test of time. Since opening our doors in Wellesley in 1970, we have had the
                privilege of dressing some of the most discerning clients in Massachusetts and
                New England.
              </p>
              <p className="text-gray-500 text-lg leading-relaxed mb-5">
                We started as a small bespoke tailoring studio and grew entirely through word of mouth —
                one perfectly fitted suit at a time. Our clients return not just because of the
                quality of our garments, but because of the relationships we build and the
                deeply personalized experience we deliver at every visit.
              </p>
              <p className="text-gray-500 text-lg leading-relaxed">
                Today, our showroom at 326A Washington Street in Wellesley is a welcoming space
                where you can explore 5,000+ premium fabrics, work one-on-one with our master
                tailors, and experience what it truly means to wear a garment made entirely for you.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden w-full h-[580px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/59735c384db81eefeb9c345ac558967f.jpg"
                  alt="Custom Clothiers showroom interior — Wellesley MA"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#C4A962] text-white rounded-2xl p-6 w-48 text-center">
                <p className="text-5xl font-bold leading-none">1970</p>
                <p className="text-sm mt-2 text-white/85 font-medium">Est. Wellesley, MA</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-4">
              Our Core Values
            </p>
            <h2 className="text-4xl font-bold text-gray-900 mb-5">What We Stand For</h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Our values guide every garment we create and every client relationship we build.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div
                key={v.title}
                className="bg-white rounded-3xl p-9 border border-gray-100 text-center hover:border-[#C4A962]/30 hover:shadow-sm transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#C4A962]/10 rounded-2xl mx-auto mb-6">
                  <i className={`${v.icon} text-[#C4A962] text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-28 bg-[#1a1a1a] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="rounded-3xl overflow-hidden w-full h-[640px]">
              <img
                src="https://readdy.ai/api/search-image?query=seasoned%20master%20tailor%20in%20his%2060s%20wearing%20a%20vest%20and%20dress%20shirt%20focused%20intently%20on%20hand%20stitching%20the%20silk%20lining%20of%20a%20fine%20charcoal%20suit%20jacket%20in%20a%20warm%20traditional%20workshop%20atelier%20needle%20thread%20thimble%20on%20finger%20shelves%20of%20tools%20and%20fabric%20behind%20him%20artisan%20craftsman%20portrait%20photography%20golden%20hour%20warm%20bokeh%20bespoke%20Massachusetts%20clothier&width=900&height=1280&seq=timeline-elite-0323&orientation=portrait"
                alt="Master tailor hand-crafting a bespoke suit at Custom Clothiers"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-4">
                Our Journey
              </p>
              <h2 className="text-4xl font-bold text-white mb-3">55+ Years of Craftsmanship</h2>
              <p className="text-gray-400 text-lg mb-14 leading-relaxed">
                Growth, community, and an unwavering commitment to the art of bespoke tailoring.
              </p>
              <div className="space-y-0">
                {milestones.map((m, i) => (
                  <div key={m.year} className="flex items-start gap-6 group">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-5 h-5 rounded-full bg-[#C4A962] group-hover:scale-125 transition-transform flex-shrink-0 mt-1"></div>
                      {i < milestones.length - 1 && (
                        <div className="w-px h-14 bg-[#C4A962]/20 mt-1"></div>
                      )}
                    </div>
                    <div className="pb-6">
                      <span className="text-[#C4A962] font-bold text-base">{m.year}</span>
                      <p className="text-gray-300 text-sm leading-relaxed mt-1">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CompanyStorySection;
